import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";

export interface ArbitrationCaseItem {
  id?: number;
  case_number?: string;
  project?: number;
  district?: number;
  officer?: number;
  project_name?: string;
  district_name?: string;
  officer_name?: string;
  total_cases?: number;
  cases_on_hearing?: number;
  disposed_cases?: number;
  pending_cases?: number;
  comments?: string;
  register_date?: string;
}

export interface ArbitrationDashboardSummary {
  total_entries?: number;
  total_cases?: number;
  cases_on_hearing?: number;
  disposed_cases?: number;
  pending_cases?: number;
}

export interface ArbitrationActivityItem {
  id?: number;
  case?: number;
  activity_type?: string;
  message?: string;
  created_at?: string;
  user?: any;
  user_name?: string;
}

export interface ArbitrationDistrictItem {
  id: number;
  name: string;
}

export interface ArbitrationOfficerItem {
  id: number;
  name: string;
  email?: string;
}

export interface ArbitrationCasePayload {
  project: number;
  district: number;
  officer: number;
  total_cases: number;
  cases_on_hearing: number;
  disposed_cases: number;
  comments?: string;
  register_date: string;
}

export interface ArbitrationActivityPayload {
  case: number;
  activity_type: string;
  message: string;
}

export interface ArbitrationCaseQuery {
  project?: string;
  district?: string;
  officer?: string;
  search?: string;
}

@Injectable({ providedIn: "root" })
export class ArbitrationService {
  private readonly baseUrl = environment.baseUrl + "/api/arbitration/v1";

  constructor(private http: HttpClient) {}

  listCases(query?: ArbitrationCaseQuery): Observable<ArbitrationCaseItem[]> {
    return this.http.get<ArbitrationCaseItem[]>(this.baseUrl + "/cases/", {
      params: this.buildParams(query),
    });
  }

  getCase(id: number | string): Observable<ArbitrationCaseItem> {
    return this.http.get<ArbitrationCaseItem>(this.baseUrl + "/cases/" + id + "/");
  }

  createCase(payload: ArbitrationCasePayload): Observable<ArbitrationCaseItem> {
    return this.http.post<ArbitrationCaseItem>(this.baseUrl + "/cases/", payload);
  }

  updateCase(id: number | string, payload: ArbitrationCasePayload): Observable<ArbitrationCaseItem> {
    return this.http.put<ArbitrationCaseItem>(this.baseUrl + "/cases/" + id + "/", payload);
  }

  deleteCase(id: number | string): Observable<any> {
    return this.http.delete(this.baseUrl + "/cases/" + id + "/");
  }

  getDashboard(query?: ArbitrationCaseQuery): Observable<ArbitrationDashboardSummary> {
    return this.http.get<ArbitrationDashboardSummary>(this.baseUrl + "/dashboard/", {
      params: this.buildParams(query),
    });
  }

  listDistricts(): Observable<ArbitrationDistrictItem[]> {
    return this.http.get<ArbitrationDistrictItem[]>(this.baseUrl + "/districts/");
  }

  listOfficers(): Observable<ArbitrationOfficerItem[]> {
    return this.http.get<ArbitrationOfficerItem[]>(this.baseUrl + "/officers/");
  }

  listActivities(caseId: number | string): Observable<ArbitrationActivityItem[]> {
    const params = new HttpParams().set("case_id", String(caseId));
    return this.http.get<ArbitrationActivityItem[]>(this.baseUrl + "/activity/", { params });
  }

  createActivity(payload: ArbitrationActivityPayload): Observable<ArbitrationActivityItem> {
    return this.http.post<ArbitrationActivityItem>(this.baseUrl + "/activity/create/", payload);
  }

  private buildParams(query?: ArbitrationCaseQuery): HttpParams {
    let params = new HttpParams();

    if (query?.project) params = params.set("project", query.project);
    if (query?.district) params = params.set("district", query.district);
    if (query?.officer) params = params.set("officer", query.officer);
    if (query?.search) params = params.set("search", query.search);

    return params;
  }
}
